import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { clientsApi } from '../api/clients';
import { normalizePaginatedResponse } from '../lib/pagination';
import { Client } from '../types/client';

interface UseClientsParams {
  search?: string;
  page?: number;
  limit?: number;
}

export function useClients(params?: UseClientsParams) {
  return useQuery({
    queryKey: ['clients', params?.search, params?.page, params?.limit],
    queryFn: async () => {
      const response = await clientsApi.getAll({
        search: params?.search,
        page: params?.page,
        limit: params?.limit,
      });
      return normalizePaginatedResponse<Client>(response);
    },
    staleTime: 1000 * 60 * 2,
    placeholderData: keepPreviousData,
  });
}

export function useClient(id: number) {
  return useQuery({
    queryKey: ['clients', id],
    queryFn: async () => {
      const response = await clientsApi.getById(id);
      return response || null;
    },
    enabled: !!id && id > 0,
    staleTime: 1000 * 60 * 2,
  });
}

export function useCreateClient() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => clientsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      queryClient.invalidateQueries({ queryKey: ['reports'] });
    },
    onError: (_error: any) => {
      // Error ya manejado por react-query o por el componente
    },
  });
}

export function useUpdateClient() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) =>
      clientsApi.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      queryClient.invalidateQueries({ queryKey: ['clients', id] });
      queryClient.invalidateQueries({ queryKey: ['reports'] });
    },
    onError: (_error: any) => {
      // Error ya manejado por react-query o por el componente
    },
  });
}

export function useDeleteClient() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => clientsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      queryClient.invalidateQueries({ queryKey: ['reports'] });
    },
    onError: (_error: any) => {
      // Error ya manejado por react-query o por el componente
    },
  });
}